import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ventasAPI, lotesAPI } from '../lib/api';
import { formatNumber, formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Receipt, Scales, Calculator } from '@phosphor-icons/react';

export default function VentaForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedLoteId = searchParams.get('lote_id');
  
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingLotes, setLoadingLotes] = useState(true);
  
  const [formData, setFormData] = useState({
    lote_id: preselectedLoteId || '',
    no_conduce: '',
    cliente: '',
    fecha: new Date().toISOString().split('T')[0],
    total_jaulas: '',
    cantidad_pollos: '',
    peso_tara: '',
    peso_bruto: '',
    precio_libra: '48.00',
    dias_credito: '1', // Default to 1 day
  });

  useEffect(() => {
    loadLotes();
  }, []);

  const loadLotes = async () => {
    try {
      const response = await lotesAPI.getAll('activo');
      setLotes(response.data);
      if (response.data.length === 1 && !preselectedLoteId) {
        setFormData((prev) => ({ ...prev, lote_id: response.data[0].id }));
      }
    } catch (error) {
      toast.error('Error al cargar los lotes');
    } finally {
      setLoadingLotes(false);
    }
  };

  const handleChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Calculate derived values
  const pesoNeto = (parseFloat(formData.peso_bruto) || 0) - (parseFloat(formData.peso_tara) || 0);
  const pesoPromedio = formData.cantidad_pollos ? pesoNeto / parseFloat(formData.cantidad_pollos) : 0;
  const totalFactura = pesoNeto * (parseFloat(formData.precio_libra) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.lote_id || !formData.no_conduce || !formData.cliente || 
        !formData.cantidad_pollos || !formData.peso_tara || !formData.peso_bruto || !formData.precio_libra) {
      toast.error('Por favor complete todos los campos obligatorios');
      return;
    }

    if (pesoNeto <= 0) {
      toast.error('El peso neto debe ser mayor a 0');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        lote_id: formData.lote_id,
        no_conduce: formData.no_conduce,
        cliente: formData.cliente,
        fecha: formData.fecha,
        total_jaulas: parseInt(formData.total_jaulas) || 0,
        cantidad_pollos: parseInt(formData.cantidad_pollos),
        peso_tara: parseFloat(formData.peso_tara),
        peso_bruto: parseFloat(formData.peso_bruto),
        precio_libra: parseFloat(formData.precio_libra),
        dias_credito: parseInt(formData.dias_credito) || 0,
      };
      
      await ventasAPI.create(payload);
      toast.success('Venta registrada exitosamente');
      
      if (preselectedLoteId) {
        navigate(`/lotes/${preselectedLoteId}`);
      } else {
        navigate('/ventas');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrar la venta';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-3xl mx-auto animate-fade-in" data-testid="venta-form">
        <div className="mb-6">
          <Link
            to={preselectedLoteId ? `/lotes/${preselectedLoteId}` : '/ventas'}
            className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-2"
          >
            <ArrowLeft size={14} />
            {preselectedLoteId ? 'Volver al Lote' : 'Volver a Ventas'}
          </Link>
          <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            Nueva Venta
          </h1>
          <p className="text-muted-foreground">
            Registre una factura de venta de pollos
          </p>
        </div>

        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt size={24} className="text-[#F69031]" weight="fill" />
              Información de la Venta
            </CardTitle>
            <CardDescription>
              Complete los datos de la factura
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Lote and Invoice Number */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="lote_id">Lote *</Label>
                  <Select
                    value={formData.lote_id}
                    onValueChange={(value) => handleChange('lote_id', value)}
                  >
                    <SelectTrigger data-testid="select-lote">
                      <SelectValue placeholder="Seleccione un lote" />
                    </SelectTrigger>
                    <SelectContent>
                      {lotes.map((lote) => (
                        <SelectItem key={lote.id} value={lote.id}>
                          {lote.nombre} - Día {lote.dias_transcurridos}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="no_conduce">No. Conduce / Factura *</Label>
                  <Input
                    id="no_conduce"
                    placeholder="Ej: 1237"
                    value={formData.no_conduce}
                    onChange={(e) => handleChange('no_conduce', e.target.value)}
                    data-testid="no-conduce"
                  />
                </div>
              </div>

              {/* Cliente and Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cliente">Cliente *</Label>
                  <Input
                    id="cliente"
                    placeholder="Nombre del cliente"
                    value={formData.cliente}
                    onChange={(e) => handleChange('cliente', e.target.value)}
                    data-testid="cliente"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fecha">Fecha *</Label>
                  <Input
                    id="fecha"
                    type="datetime-local"
                    value={formData.fecha.includes('T') ? formData.fecha : `${formData.fecha}T12:00`}
                    onChange={(e) => handleChange('fecha', e.target.value)}
                    data-testid="fecha"
                  />
                </div>
              </div>

              {/* Jaulas and Pollos */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="total_jaulas">Total Uni. Jaulas</Label>
                  <Input
                    id="total_jaulas"
                    type="number"
                    min="0"
                    placeholder="Ej: 315"
                    value={formData.total_jaulas}
                    onChange={(e) => handleChange('total_jaulas', e.target.value)}
                    data-testid="total-jaulas"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cantidad_pollos">Cant. Pollos Und. *</Label>
                  <Input
                    id="cantidad_pollos"
                    type="number"
                    min="1"
                    placeholder="Ej: 2205"
                    value={formData.cantidad_pollos}
                    onChange={(e) => handleChange('cantidad_pollos', e.target.value)}
                    data-testid="cantidad-pollos"
                  />
                </div>
              </div>

              {/* Weights */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="peso_tara">Peso Tara (lb) *</Label>
                  <Input
                    id="peso_tara"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ej: 3550.00"
                    value={formData.peso_tara}
                    onChange={(e) => handleChange('peso_tara', e.target.value)}
                    data-testid="peso-tara"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="peso_bruto">Peso Bruto (lb) *</Label>
                  <Input
                    id="peso_bruto"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Ej: 14733.00"
                    value={formData.peso_bruto}
                    onChange={(e) => handleChange('peso_bruto', e.target.value)}
                    data-testid="peso-bruto"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Peso Neto (lb)</Label>
                  <div className="h-10 px-3 py-2 bg-muted/50 rounded-md flex items-center font-semibold text-emerald-600">
                    <Scales size={18} className="mr-2" />
                    {formatNumber(pesoNeto)} lb
                  </div>
                </div>
              </div>

              {/* Price and Credit */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="precio_libra">Precio por Libra ($) *</Label>
                  <Input
                    id="precio_libra"
                    type="number"
                    step="0.01"
                    min="0.01"
                    placeholder="Ej: 48.00"
                    value={formData.precio_libra}
                    onChange={(e) => handleChange('precio_libra', e.target.value)}
                    data-testid="precio-libra"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dias_credito">Días de Crédito</Label>
                  <Select
                    value={formData.dias_credito}
                    onValueChange={(value) => handleChange('dias_credito', value)}
                  >
                    <SelectTrigger data-testid="select-credito">
                      <SelectValue placeholder="Seleccione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1 día</SelectItem>
                      <SelectItem value="3">3 días</SelectItem>
                      <SelectItem value="7">7 días</SelectItem>
                      <SelectItem value="14">14 días</SelectItem>
                      <SelectItem value="21">21 días</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Peso Promedio</Label>
                  <div className="h-10 px-3 py-2 bg-muted/50 rounded-md flex items-center font-semibold">
                    {pesoPromedio.toFixed(2)} lb/pollo
                  </div>
                </div>
              </div>

              {/* Total Preview */}
              <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-6 border border-[#E73425]/20">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground uppercase tracking-wider">Total a Pagar</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatNumber(pesoNeto)} lb × ${formData.precio_libra || '0.00'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-bold text-[#E73425]" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                      {formatCurrency(totalFactura)}
                    </p>
                    {parseInt(formData.dias_credito) > 0 && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Crédito: {formData.dias_credito} días
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Summary Info */}
              {formData.cantidad_pollos && pesoNeto > 0 && (
                <div className="grid grid-cols-3 gap-4 text-center text-sm">
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground">Pollos</p>
                    <p className="font-semibold">{formatNumber(parseInt(formData.cantidad_pollos))}</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground">Peso Neto</p>
                    <p className="font-semibold">{formatNumber(pesoNeto)} lb</p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-muted-foreground">Promedio</p>
                    <p className="font-semibold">{pesoPromedio.toFixed(2)} lb</p>
                  </div>
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(preselectedLoteId ? `/lotes/${preselectedLoteId}` : '/ventas')}
                  className="flex-1"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-[#E73425] hover:bg-[#D81C23] btn-animate"
                  disabled={loading}
                  data-testid="submit-venta"
                >
                  {loading ? (
                    <div className="spinner h-5 w-5" />
                  ) : (
                    <>
                      <Receipt size={18} className="mr-2" />
                      Registrar Venta
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
